<!-- src/views/PendingAgentViewerById.vue -->
<template>
  <v-container max-width="1400" class="py-10">
    <v-row class="mb-6" align="center" justify="space-between">
      <v-col cols="12" md="8">
        <div class="text-h4 font-weight-bold">Pending Agent Signup</div>
        <div class="text-body-1 text-medium-emphasis">
          Review and manage this submission (ID: {{ id }})
        </div>
      </v-col>
      <v-col cols="12" md="4" class="d-flex justify-end ga-2">
        <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="goBack">Back</v-btn>
        <v-btn color="success" class="text-white" :loading="busy.approve" :disabled="!row" @click="onApprove">
          <v-icon start>mdi-check</v-icon> Approve
        </v-btn>
        <v-btn color="error" variant="tonal" :loading="busy.deny" :disabled="!row" @click="onDeny">
          <v-icon start>mdi-close</v-icon> Deny
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading / Not found -->
    <v-skeleton-loader v-if="loading" type="article, table, image" class="mb-6" />
    <v-alert v-else-if="!row" type="warning" variant="tonal">
      No submission found for id <strong>{{ id }}</strong>.
    </v-alert>

    <!-- Details -->
    <template v-else>
      <v-row>
        <v-col cols="12" md="5">
          <!-- Submission Details -->
          <v-card elevation="2" rounded="lg" class="mb-4" v-if="row">
            <v-card-title class="text-subtitle-1 font-weight-bold">Submission Details</v-card-title>
            <v-divider />
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Created At</div>
                  <div class="text-body-2">{{ fmtDate(row.created_at) || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Submission ID (UUID)</div>
                  <div class="text-body-2">{{ row.id }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Agency Name</div>
                  <div class="text-body-2">{{ row.agency_name || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">IATA / CLIA</div>
                  <div class="text-body-2">{{ row.ita_number || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">First Name</div>
                  <div class="text-body-2">{{ row.first_name || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Last Name</div>
                  <div class="text-body-2">{{ row.last_name || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Email</div>
                  <div class="text-body-2">{{ row.email || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Phone</div>
                  <div class="text-body-2">{{ row.phone || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="12" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Street Address</div>
                  <div class="text-body-2">{{ row.street || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="4" class="mb-2">
                  <div class="text-caption text-medium-emphasis">City</div>
                  <div class="text-body-2">{{ row.city || '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Postal Code</div>
                  <div class="text-body-2">{{ row.postal_code || '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Region/State</div>
                  <div class="text-body-2">{{ row.region || '—' }}</div>
                </v-col>

                <v-col cols="12" sm="6" class="mb-2">
                  <div class="text-caption text-medium-emphasis">Desired Plan</div>
                  <div class="text-body-2">{{ row.desired_plan || '—' }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="7">
          <!-- Documents (Tabbed, large inline preview) -->
          <v-card elevation="2" rounded="lg" v-if="row">
            <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center justify-space-between">
              <span>Documents</span>
              <small class="text-medium-emphasis" v-if="!docItems.length">No documents uploaded</small>
            </v-card-title>
            <v-divider />

            <template v-if="docItems.length">
              <!-- Tabs header -->
              <v-tabs v-model="docTab" align-tabs="start" class="px-2" density="comfortable">
                <v-tab v-for="(d, i) in docItems" :key="d.key" :value="i">
                  <v-icon start class="mr-1">mdi-file-document</v-icon> {{ d.title }}
                </v-tab>
              </v-tabs>
              <v-divider />

              <!-- Tab panels -->
              <v-window v-model="docTab" class="pa-4">
                <v-window-item v-for="(d, i) in docItems" :key="d.key" :value="i">
                  <FileViewerInline :title="d.title" :path="d.path" bucket="agent-documents" height="100vh" />
                </v-window-item>
              </v-window>
            </template>

            <v-card-text v-else>
              <v-alert type="info" variant="tonal">This submission has no documents.</v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3500" location="top center">
      {{ snack.msg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import { useRoute, useRouter } from 'vue-router'
import FileViewerInline from '@/components/FileViewerInline.vue'

export default {
  name: 'PendingAgentViewerById',
  components: {
    FileViewerInline
  },


  setup() {
    const route = useRoute()
    const router = useRouter()
    return { route, router }
  },

  data() {
    return {
      id: this.route.params.uid || '',
      loading: true,
      row: null,
      signed: { agreement: '', license: '', w9: '' },
      signing: { agreement: false, license: false, w9: false },
      busy: { approve: false, deny: false },
      snack: { show: false, color: 'success', msg: '' },
      bucket: 'agent-documents',
      docTab: 0, // active tab index
    }
  },

  computed: {
    docItems() {
      if (!this.row) return []
      return [
        { key: 'agreement', title: 'Advisor Agreement', path: this.row.advisor_agreement_url },
        { key: 'license', title: 'Travel License', path: this.row.travel_license_url },
        { key: 'w9', title: 'W-9 Form', path: this.row.w9_url },
      ].filter(d => !!d.path) // only show tabs that actually have a file
    }
  },
  watch: {
    // reset to first tab when row changes
    row() { this.docTab = 0 }
  },

  async created() {
    if (!this.id) {
      this.loading = false
      this.toast('Missing id parameter.', 'error')
      return
    }
    await this.loadRow()
  },

  methods: {
    async loadRow() {
      this.loading = true
      this.row = null
      this.signed = { agreement: '', license: '', w9: '' }

      try {
        const { data, error } = await supabase
          .from('pending_agent_signups')
          .select('*')
          .eq('id', this.id)
          .single()
        if (error) throw error
        this.row = data
        console.log(this.row)
      } catch (e) {
        console.error('loadRow error:', e)
        this.row = null
      } finally {
        this.loading = false
      }
    },
    async signDoc(which, path) {
      if (!path) return
      try {
        this.signing[which] = true
        const { data, error } = await supabase
          .storage
          .from(this.bucket)
          .createSignedUrl(path, 600) // 10 minutes
        if (error) throw error
        this.signed[which] = data?.signedUrl || ''
      } catch (e) {
        console.error('signed url error:', e)
        this.snack = { show: true, color: 'error', msg: 'Could not generate signed link.' }
      } finally {
        this.signing[which] = false
      }
    },

    async getSigned(which, path) {
      if (!path) return
      try {
        this.signing[which] = true
        const { data, error } = await supabase
          .storage
          .from(this.bucket)
          .createSignedUrl(path, 600) // 10 minutes
        if (error) throw error
        this.signed[which] = data?.signedUrl || ''
      } catch (e) {
        console.error('signed url error:', e)
        this.toast('Could not generate signed link.', 'error')
      } finally {
        this.signing[which] = false
      }
    },

    async onApprove() {
      if (!this.row) return
      this.busy.approve = true
      try {
        const r = this.row
        console.log(r)
        // 0) Who is approving? (for audit)
        const { data: meData } = await supabase.auth.getUser()
        const approverUid = meData?.user?.id || null

        // 1) Decide which uid to use for users(uid)
        let targetUid = r.id

        // Try to find an existing user by email if we don't have a uid yet
        if (!targetUid && r.email) {
          const { data: existingByEmail, error: emailErr } = await supabase
            .from('users')
            .select('uid')
            .eq('email', r.email)
            .limit(1)
            .maybeSingle()
          if (!emailErr && existingByEmail?.uid) {
            targetUid = existingByEmail.uid
          }
        }

        // If still no uid, generate a placeholder one.
        // NOTE: This won't match their future Auth UID. Consider adding
        // a reconciliation flow later when/if they sign up.
        if (!targetUid) {
          targetUid = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : (await import('uuid')).v4() // fallback if needed
        }

        const now = new Date().toISOString()

        // 2) Build payload from pending -> users
        const userPayload = {
          uid: targetUid,
          first_name: r.first_name || null,
          last_name: r.last_name || null,
          email: r.email || null,
          phone: r.phone || null,

          // flags
          is_agent: true,
          agent_approved_at: now,
          agent_approved_by: approverUid,

          // business data
          agency_name: r.agency_name || null,
          ita_number: r.ita_number || null,

          // address
          city: r.city || null,
          region: r.region || null,
          postal_code: r.postal_code || null,

          // docs (kept as URLs/paths)
          advisor_agreement_url: r.advisor_agreement_url || null,
          travel_license_url: r.travel_license_url || null,
          w9_url: r.w9_url || null,

          // plan seed
          membership_plan: r.desired_plan || null,

          // created_at will default for new rows; but we also set it on insert via upsert
          created_at: now,
        }

        // 3) Create or update the user row
        // Requires UNIQUE or PRIMARY KEY on users.uid (you have it).
        // If you also add a unique constraint on email, remove it from onConflict.
        const { error: upErr } = await supabase
          .from('users')
          .upsert(userPayload, { onConflict: 'uid' })
        if (upErr) throw upErr

        // 4) Remove pending row
        const { error: delErr } = await supabase
          .from('pending_agent_signups')
          .delete()
          .eq('id', r.id)
        if (delErr) throw delErr

        this.toast('Application approved.', 'success')
        // brief tick so the snackbar can render, then go back
        await new Promise(resolve => setTimeout(resolve, 2000))
        this.goBack()

      } catch (e) {
        console.error('approve error:', e)
        this.toast(e?.message || 'Failed to approve application.', 'error')
      } finally {
        this.busy.approve = false
      }
    },

    async onDeny() {
      if (!this.row) return
      this.busy.deny = true
      try {
        const { error } = await supabase
          .from('pending_agent_signups')
          .delete()
          .eq('id', this.row.id)
        if (error) throw error

        this.toast('Application denied and removed.', 'success')
        // brief tick so the snackbar can render, then go back
        await new Promise(resolve => setTimeout(resolve, 2000))
        this.goBack()
      } catch (e) {
        console.error('deny error:', e)
        this.toast('Failed to deny application.', 'error')
      } finally {
        this.busy.deny = false
      }
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/dashboard/pending-agents')
      }
    },


    fmtDate(d) {
      if (!d) return '—'
      try { return new Date(d).toLocaleString() } catch { return String(d) }
    },

    toast(msg, color = 'success') {
      this.snack = { show: true, color, msg }
    }
  }
}
</script>

<style scoped>
.file-frame {
  height: 320px;
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 8px;
  overflow: hidden;
}

.file-frame iframe {
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
